import React from "react";
import tailwindClassnames from "../common/TailwindClassnames";
import { t } from "i18next";
import SelectInput from "../common/SelectInput";
import TextInput from "../common/TextInput";

type SearchBarProjectProps = {
  countries: any[];
  onChangeSearchQuery: Function;
  onSearch: Function;
  tempProjectsSearchQuery: any;
  orgProjectsTab?: boolean;
  onAddProject?: () => void;
};
/**
 * Filter toolbar for the projects table card. Renders the name/organization
 * (or project-admin-email) text filters, the country dropdown, the apply
 * button, and — when `onAddProject` is supplied — the "Add Project" action at
 * the far right. Expects to be rendered as the first child of a
 * `tailwindClassnames.tableCard` container so its bottom border attaches it
 * to the table below.
 */
// Widened variant of the shared filter-input width, local to this toolbar so the
// name/organization/admin-email filters get extra room without affecting the
// `tableFilterInput` class used by other table toolbars.
const wideTableFilterInput = tailwindClassnames.tableFilterInput.replace(
  "w-44",
  "w-56",
);

const SearchBarProjects = ({
  countries,
  onChangeSearchQuery,
  onSearch,
  tempProjectsSearchQuery,
  orgProjectsTab,
  onAddProject,
}: SearchBarProjectProps) => {
  const onCountrySelection = async (e: any) => {
    await onChangeSearchQuery(
      e
        ? {
            target: {
              name: "country_id",
              value: e.value,
            },
          }
        : null,
    );
  };

  return (
    <div className={tailwindClassnames.tableCardToolbar}>
      <TextInput
        name={"project_name"}
        label={t("Name")}
        placeholder={t("Search project name")}
        value={tempProjectsSearchQuery.project_name || ""}
        error={""}
        disabled={false}
        className={wideTableFilterInput}
        type={"text"}
        initialFocus={true}
        onChange={(e: any) => onChangeSearchQuery(e)}
        onKeyPress={(e) => {
          if (e.key === "Enter") {
            onSearch();
          }
        }}
      />
      {!orgProjectsTab ? (
        <TextInput
          name={"org_name"}
          label={t("Organization")}
          placeholder={t("Search organization name")}
          value={tempProjectsSearchQuery.org_name || ""}
          error={""}
          disabled={false}
          className={wideTableFilterInput}
          type={"text"}
          initialFocus={false}
          onChange={(e: any) => onChangeSearchQuery(e)}
          onKeyPress={(e) => {
            if (e.key === "Enter") {
              onSearch();
            }
          }}
        />
      ) : (
        <TextInput
          name={"primary_contact_email"}
          label={t("Project Admin Email")}
          placeholder={t("Search project admin email")}
          value={tempProjectsSearchQuery.primary_contact_email || ""}
          error={""}
          disabled={false}
          className={wideTableFilterInput}
          type={"text"}
          initialFocus={false}
          onChange={(e: any) => onChangeSearchQuery(e)}
          onKeyPress={(e) => {
            if (e.key === "Enter") {
              onSearch();
            }
          }}
        />
      )}
      <SelectInput
        options={countries.map((country) => ({
          value: country.code,
          label: country.name,
        }))}
        className={"w-48 text-left text-sm"}
        isDisabled={false}
        isLoading={false}
        isClearable={true}
        isSearchable={true}
        isMulti={false}
        name={t("Country")}
        onChange={(e: any) => onCountrySelection(e)}
        errors={""}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === "Enter") {
            onSearch();
          }
        }}
        searchBar={true}
      />
      <button
        className={tailwindClassnames.btnFilterApply}
        onClick={() => onSearch()}
      >
        {t("Apply filters")}
      </button>
      <div className="flex-1" />
      {/* Tab layouts and the top-level Projects page both pass onAddProject
          and render the create action at the far right of this toolbar
          (matching the Users tab). Conditional rendering, not a "hidden"
          class swap, so the permission gate can't be defeated by display
          utilities. */}
      {onAddProject && (
        <button
          onClick={() => onAddProject()}
          className={tailwindClassnames.btnPrimary}
        >
          <span aria-hidden="true">+ </span>
          {t("Add Project")}
        </button>
      )}
    </div>
  );
};

export default SearchBarProjects;
